// «Чернила» — читаемый вариант сигнального цвета для ТЕКСТА.
//
// ЗАЧЕМ. Сигнальная палитра портала (#10b981, #f59e0b, #ef4444, #06b6d4,
// #8b5cf6, --success/--danger/--warning) подобрана под ЗАЛИВКУ. Тот же цвет,
// взятый как цвет текста поверх бледной плашки на светлой теме, читается
// дымкой: замер браузером 17.08.2026 дал 2.0–3.8:1 при норме WCAG AA 4.5:1
// (подписи «В ДВИЖЕНИИ», «ТЕКУЧЕСТЬ», строки статусов сотрудников в HR).
//
// ПРИЁМ. Опускаем ТОЛЬКО светлоту, тон и насыщенность не трогаем (в oklch
// меняем `l`, оставляем `c` и `h`). И не на общую долю, а по ПОТОЛКУ:
// `min(l, --ink-l)`. Цвет, который и так достаточно тёмный, не меняется вовсе;
// слишком светлый опускается ровно до потолка. Красный остаётся красным,
// янтарный — янтарным, и каждый темнеет настолько, насколько ему нужно.
//
// ⚠️ Первая версия (17.08, отвергнута) подмешивала к цвету сам --text. Он
// тёмно-синий (#16113a), поэтому красный уезжал в мутную сливу. Хуже того,
// доля была ОБЩЕЙ и подобранной по худшему цвету (янтарь #f59e0b), так что
// остальные темнели вдвое сильнее нужного: --danger #dc2626 сам по себе даёт
// 4,34:1 и ему хватало пары процентов. Максим: «сильно затемнились надписи».
//
// ВАЖНО: применять только к ТЕКСТУ и иконкам-глифам. Заливки, рамки, линии
// графиков и сегменты диаграмм красить исходным цветом — там контраст с фоном
// обеспечивает сама площадь, а подмешивание сделает палитру грязной.
//
// Для готовых токенов темы отдельный вызов не нужен — в globals.css уже есть
// --successInk / --warningInk / --dangerInk / --primaryInk / --accentInk.

/** Читаемый на текущей теме вариант цвета — для текста и иконок-глифов. */
export const ink = (c: string) => `oklch(from ${c} min(l, var(--ink-l)) c h)`;

/** Относительная яркость цвета по WCAG. Понимает #rgb и #rrggbb. */
function luminance(hex: string): number | null {
  const m = hex.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].replace(/./g, (d) => d + d) : m[1];
  const ch = [0, 2, 4].map((i) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/**
 * Цвет надписи, читаемый НА сплошной заливке `bg`: белый на тёмном, почти
 * чёрный на светлом. Для случаев, где заливка приходит из карты цветов и
 * заранее неизвестно, светлая она или тёмная.
 *
 * ЗАЧЕМ. Счётчик непрочитанного на кнопке помощи красился белым по цвету
 * источника: на фиолетовом #b14bff это 3.9:1, а на зелёном #39e23f — 1.8:1,
 * то есть цифра практически исчезала. Порог 0.1833 — ровно та яркость, при
 * которой белый текст даёт 4.5:1.
 */
export function onColor(bg: string, dark = "#101010", light = "#ffffff"): string {
  const l = luminance(bg);
  if (l === null) return light; // не разобрали цвет — прежнее поведение
  return l > 0.1833 ? dark : light;
}
